import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { getDefaultAreaType } from 'screens/Applications&WorkOrders/StepsLayout/Constants/areaTypeConstants';
import { calculateTotalAppliedAcreage } from 'screens/Applications&WorkOrders/StepsLayout/Components/PropertiesStep/helpers/cropZoneMath';
import { getPlanProperties } from 'screens/Plans/helpers/dataHelpers';
import { shouldShowPlanCropSeasonMismatch } from 'helpers/cropSeasonMismatch';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import { PlanWizardFormValues, PlanCropZone } from '../../../types';
import { usePropertyTreeContext } from '../../PropertyTreeContext';
import { useCropDataContext } from '../../CropDataContext';
import applyPlannedAreaChange from '../helpers/applyPlannedAreaChange';

/**
 * Re-resolves the property tree's `crop` by id against the canonical crop
 * list (`useCropDataContext`) — the two backends' names can differ in case,
 * which would break `CropIconMapping`'s case-sensitive lookup.
 */
const resolveCanonicalCrop = (
  cropFromTree: PlanCropZone['crop'],
  allCrops?: Array<{ id: string; name: string }>
): PlanCropZone['crop'] => {
  if (!cropFromTree) return cropFromTree;
  const canonical = allCrops?.find(crop => crop.id === cropFromTree.id);
  return canonical ? { id: canonical.id, name: canonical.name } : cropFromTree;
};

/**
 * On Edit/Duplicate, a fetched Plan's persisted `cropZones` only carry
 * `id`/`area`/`coveragePercent`/`cropSeasonId` (see `mapFetchedCropZonesToStubs`
 * in `planFormMapping.ts`) — this fills in the rest of each zone's display
 * fields from the property tree (`getPlanProperties`, as `ConvertPlanToRecModal`
 * already does), and drops any zone whose crop season is no longer
 * breadcrumb-selected, recalculating Planned Area/Fixed Cost/products via
 * `applyPlannedAreaChange`.
 */
const useResolveStubCropZones = (): void => {
  const { values, setFieldValue } = useFormikContext<PlanWizardFormValues>();
  const {
    propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    loading
  } = usePropertyTreeContext();
  const { crops, loading: cropsLoading } = useCropDataContext();
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();

  const filteredRef = useRef(false);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (loading || cropsLoading) return;

    let cropZones = values.cropZones ?? [];
    let didChange = false;

    if (!filteredRef.current && values.isEditOrDuplicateMode) {
      filteredRef.current = true;
      if (shouldShowPlanCropSeasonMismatch(values, selectedCropSeasonIds)) {
        cropZones = cropZones.filter(
          zone =>
            !zone.cropSeasonId ||
            selectedCropSeasonIds.includes(zone.cropSeasonId)
        );
        const newTotal = calculateTotalAppliedAcreage(cropZones);
        setFieldValue('totalAppliedAcreage', newTotal);
        applyPlannedAreaChange(
          { ...values, cropZones },
          setFieldValue,
          newTotal
        );
        didChange = true;
      }
    }

    if (!resolvedRef.current) {
      const idsNeedingResolution = cropZones
        .filter(zone => !zone.name)
        .map(zone => zone.id);

      if (
        idsNeedingResolution.length &&
        propertyLandingPageData?.properties?.length
      ) {
        const resolved: Array<Record<string, unknown>> = getPlanProperties(
          propertyLandingPageData,
          idsNeedingResolution,
          fieldsAndAreasGeoJSONCollection ?? {}
        );

        if (resolved.length) {
          resolvedRef.current = true;

          const resolvedById = new Map(
            resolved.map(zone => [zone.id as string, zone])
          );
          cropZones = cropZones.map(zone => {
            const match = resolvedById.get(zone.id);
            if (!match) return zone;
            const reportedArea = match.reportedArea as number | undefined;
            return {
              ...zone,
              name: match.name as string | undefined,
              crop: resolveCanonicalCrop(
                match.crop as PlanCropZone['crop'],
                crops
              ),
              fieldId: match.fieldId as string | undefined,
              fieldName: match.fieldName as string | undefined,
              propertyId: match.farmId as string | undefined,
              propertyName: match.farmName as string | undefined,
              // FR-019: these three were previously dropped, leaving the
              // shape column and edit action permanently blank.
              geometry: match.geometry as PlanCropZone['geometry'],
              fieldGeometry: match.features as PlanCropZone['fieldGeometry'],
              reportedArea,
              boundaryArea:
                (match.boundaryArea as number | undefined) ?? zone.area,
              selectedAreaType:
                zone.selectedAreaType || getDefaultAreaType(reportedArea)
            };
          });
          didChange = true;
        }
      }
    }

    if (didChange) {
      setFieldValue('cropZones', cropZones);
    }
    // Deliberately narrower than `values`, to avoid re-scanning the full
    // crop-zones array on every unrelated keystroke while `resolvedRef` is
    // still unsettled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.cropZones,
    values.isEditOrDuplicateMode,
    propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    loading,
    setFieldValue,
    crops,
    cropsLoading,
    selectedCropSeasonIds
  ]);
};

export default useResolveStubCropZones;
